/**
 * A property's GDS codes — the one group both hotel and agreement drawers show.
 *
 * Extracted 2026-09-11 (item 69, cut 3). The audit found the two drawers had
 * hand-rolled 68 `<Field>`s between them over the same `hotel_master` record,
 * with different labels, different order and — the part that cost something —
 * different ORDER. The agreement drawer had scattered its codes through the
 * Property group while the hotel drawer kept them in a group of their own.
 *
 * They still differ in one field, and that difference is deliberate: Worldspan
 * is shown on the hotel panel and withheld on the agreement panel, because
 * Stacie asked for it gone there. Reinstating it "to end the divergence" is
 * exactly the mistake this component invites, which is why the prop documents
 * it and a test pins it.
 *
 * ── Only THIS group is shared, deliberately ─────────────────────────────────
 * The hotel drawer's Property group carries address, postal code, country and
 * coordinates. The agreement detail endpoint does not send any of them —
 * `HotelMasterRef` is canonicalId, name, city, state, phone, the three codes,
 * gdsSystemsUsed and brand — so a shared Property group would render four empty
 * fields on every agreement. In this panel an empty `Field` is a SIGNAL, read as
 * "this hotel has no address on file", and that would be a false one: the fact
 * is not missing, it was never asked for. Sharing the rest is an API change
 * first, and it is not this change.
 *
 * A blank code here IS a real finding, which is why nothing is hidden: a
 * property carrying a Sabre code but no Worldspan code is a known cause of a
 * load failing.
 */
import { Field, Group } from '@/features/crm/components/DetailPanel';

export interface GdsCodeValues {
  sabre: string | null | undefined;
  travelport: string | null | undefined;
  /**
   * OMITTED ENTIRELY when the key is absent, which is how the agreement drawer
   * renders it — not because that drawer lacks the data, but because Stacie
   * withdrew the field there on 2026-08-31: "Can we remove all world span to
   * everything… isn't that system dying?"
   *
   * This is the one difference between the two surfaces, and it is a product
   * decision rather than drift, so it lives here where the next person sharing
   * this component will read it. `AgreementsPage.render.spec` asserts the
   * absence, which is what caught the attempt to reinstate it.
   */
  worldspan?: string | null;
  gdsSystems: string | null | undefined;
  /**
   * The CHAIN's code — HX for Hampton, MC for Marriott — which is the same
   * across GDSs, unlike the per-property numbers above. Optional because only
   * the agreement side captures it (GBTA models the pair as
   * `*_CHAINCODE` + `*_PROPCODE`).
   */
  vendorCode?: string | null;
}

export function GdsCodes({
  codes,
  testId,
}: {
  codes: GdsCodeValues;
  testId: string;
}) {
  return (
    <Group label="GDS codes" testId={testId}>
      <dl className="detail-grid">
        <Field label="Sabre" mono value={codes.sabre} />
        <Field label="Travelport" mono value={codes.travelport} />
        {codes.worldspan !== undefined && (
          <Field label="Worldspan" mono value={codes.worldspan} />
        )}
        {codes.vendorCode !== undefined && (
          <Field label="Vendor code" mono value={codes.vendorCode} />
        )}
        <Field label="GDS systems" value={codes.gdsSystems} />
      </dl>
    </Group>
  );
}
